import Alert from 'common/components/Alert'
import Button from 'common/components/Button'
import TextInput from 'common/components/TextInput'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import isEmail from 'lib/common/is-email'
import MediaHelper from 'lib/media-helper'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 50px;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  ${(props) =>
    props.responsive &&
    MediaHelper('desktop')`
    padding: 16px; 
  `}
`
const HeaderSection = styled.div`
  margin-top: 40px;
`
const BodySection = styled.div`
  margin-top: 50px;
`
const FooterSection = styled.div`
  margin-bottom: 50px;
`
const StyledLinkFooter = styled.a`
  text-decoration: none;
  font-weight: bold;
  color: ${theme.colors.orange};
`
const StyledTextInput = styled(TextInput)`
  font-weight: bold;
  ::placeholder {
    font-weight: normal;
  }
`
const StyledButton = styled(Button)`
  font-size: 16px;
`

function ForgotPasswordForm({
  responsive,
}: {
  responsive?: boolean
}): JSX.Element {
  const [enteredUsername, setEnteredUsername] = useState<string>('')
  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [focusField, setFocusField] = useState<string>('')

  const { t } = useTranslation()
  const { isLoading, errorMessage, step } = useStoreState(
    (states) => states.forgotPassword,
  )

  const { postVerificationChannel } = useStoreActions(
    (actions) => actions.forgotPassword,
  )

  const validateUsername = (value) => {
    if (isEmail(value)) {
      setIsValidated(true)
    } else {
      setIsValidated(false)
    }
  }

  const usernameInputHandler = (event) => {
    validateUsername(event.target.value)
    setEnteredUsername(event.target.value)
  }

  const formSubmissionHandler = (event) => {
    event.preventDefault()
    if (
      isValidated &&
      !isLoading &&
      step === 'forgot-password-form'
    ) {
      postVerificationChannel({
        type: 'reset_password',
        email: enteredUsername,
      })
    }
  }

  return (
    <Container
      data-testid="forgot-password-form"
      responsive={responsive}
    >
      <HeaderSection>
        <Typography type="bh2" textAlign="center">
          {t('RESET_PASSWORD', { ns: 'auth' })}
        </Typography>
        <Typography
          type="rparagraph"
          textAlign="center"
          marginTop="15px"
        >
          {t('RESET_PASSWORD_DESC', { ns: 'auth' })}
        </Typography>
      </HeaderSection>
      <form onSubmit={formSubmissionHandler}>
        <BodySection>
          <StyledTextInput
            id="username"
            width="480px"
            onChange={usernameInputHandler}
            floatingLabel="Email"
            data-testid="input-username"
            value={enteredUsername}
            placeholder={
              focusField === 'username'
                ? t('EXAMPLE_EMAIL', { ns: 'auth' })
                : ''
            }
            onFocus={() => setFocusField('username')}
            onBlur={() => setFocusField('')}
            helperText={
              enteredUsername &&
              !isValidated &&
              t('INVALID_FIELD_FORMAT', {
                ns: 'common',
                field: 'email',
              })
            }
            hideHelperText={!enteredUsername || isValidated}
            margin={
              enteredUsername && !isValidated
                ? '0 0 10px'
                : '0 0 20px'
            }
            error={enteredUsername && !isValidated}
          />
          {errorMessage && (
            <Alert
              type="error"
              margin="0 0 25px 0"
              key={errorMessage}
            >
              {errorMessage}
            </Alert>
          )}
          <StyledButton
            type="submit"
            size="large"
            disabled={!isValidated}
            data-testid="button-submit"
            fontWeight="bold"
            isLoading={isLoading}
            margin="10px 0 30px"
            block
          >
            {t('NEXT', { ns: 'common' })}
          </StyledButton>
        </BodySection>
      </form>
      <FooterSection>
        <Typography type="rh6" textAlign="center">
          <>
            {t('LOGIN_WITH_PHONE_DESC', { ns: 'auth' })}
            <StyledLinkFooter
              href={`${WEB_DESKTOP_RALALI_URL}/login`}
            >
              &nbsp;
              {t('SIGNIN', { ns: 'auth' })}
            </StyledLinkFooter>
          </>
        </Typography>
      </FooterSection>
    </Container>
  )
}

export default ForgotPasswordForm
